import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Globe, Lock, Sparkles, Loader2 } from "lucide-react";

import { ProjectVisibility, PlanType } from "@optidev/shared-types";
import { useAuth } from "@/contexts/AuthContext";
import { billingApi } from "@/lib/api/billing";
import { usePlanFeatures } from "@/hooks/useFeatures";

interface VisibilitySelectorProps {
  value: ProjectVisibility;
  onChange: (visibility: ProjectVisibility) => void;
  disabled?: boolean;
  isOpen?: boolean; // Control when to fetch plan (re-fetches on open)
  onClose?: () => void; // Optional callback to close parent modal when navigating
}

export function VisibilitySelector({
  value,
  onChange,
  disabled = false,
  isOpen = true,
  onClose,
}: VisibilitySelectorProps) {
  const { getIdToken } = useAuth();
  const { planFeatures } = usePlanFeatures();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);
  const [loadingPlan, setLoadingPlan] = useState(true);

  // Fetch subscription to get plan type
  useEffect(() => {
    if (!isOpen) return;

    const fetchPlan = async () => {
      try {
        setLoadingPlan(true);
        const token = await getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
        }
      } catch (error) {
        console.error("Failed to fetch subscription:", error);
        // Default to FREE on error (safe default)
        setPlanType(PlanType.FREE);
      } finally {
        setLoadingPlan(false);
      }
    };

    fetchPlan();
  }, [isOpen, getIdToken]);

  // Check if private visibility is allowed for current plan
  const currentPlanFeatures = planFeatures[planType] || planFeatures.free;
  const canUsePrivate = currentPlanFeatures.visibility.includes("private");

  // Handle private option click - only select if allowed
  const handlePrivateClick = () => {
    if (canUsePrivate) {
      onChange(ProjectVisibility.PRIVATE);
    }
  };

  if (loadingPlan) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {/* Public Option */}
      <button
        onClick={() => onChange(ProjectVisibility.PUBLIC)}
        disabled={disabled}
        className={`w-full px-3 py-2.5 rounded-lg transition-all duration-200 text-left border ${
          value === ProjectVisibility.PUBLIC
            ? "bg-primary/5 border-primary/30"
            : "border-transparent hover:bg-muted/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
              value === ProjectVisibility.PUBLIC
                ? "border-primary bg-primary"
                : "border-muted-foreground/40"
            }`}
          >
            {value === ProjectVisibility.PUBLIC && (
              <div className="h-1.5 w-1.5 rounded-full bg-white" />
            )}
          </div>
          <Globe className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
            value === ProjectVisibility.PUBLIC ? "text-blue-500" : "text-muted-foreground"
          }`} />
          <div className="flex-1 min-w-0">
            <span className="font-medium text-sm">Public</span>
            <p className="text-xs text-muted-foreground">
              Anyone with the link can view
            </p>
          </div>
        </div>
      </button>

      {/* Private Option */}
      <button
        onClick={handlePrivateClick}
        disabled={disabled || !canUsePrivate}
        className={`w-full px-3 py-2.5 rounded-lg transition-all duration-200 text-left border ${
          !canUsePrivate
            ? "opacity-50 cursor-not-allowed border-transparent"
            : value === ProjectVisibility.PRIVATE
              ? "bg-primary/5 border-primary/30"
              : "border-transparent hover:bg-muted/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 ${
              value === ProjectVisibility.PRIVATE && canUsePrivate
                ? "border-primary bg-primary"
                : "border-muted-foreground/40"
            }`}
          >
            {value === ProjectVisibility.PRIVATE && canUsePrivate && (
              <div className="h-1.5 w-1.5 rounded-full bg-white" />
            )}
          </div>
          <Lock className={`h-4 w-4 flex-shrink-0 transition-colors duration-200 ${
            value === ProjectVisibility.PRIVATE && canUsePrivate ? "text-amber-500" : "text-muted-foreground"
          }`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">Private</span>
              {!canUsePrivate && (
                <Link
                  to="/dashboard/settings/billing"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose?.();
                  }}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/20 transition-colors"
                >
                  <Sparkles className="h-2.5 w-2.5" />
                  PRO
                </Link>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Only accessible to workspace members and through OptiSigns displays
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}
